import { BookingServiceV2 } from 'core/api/services/bookingV2';
import { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useCurrency } from './currency';

const BookingV2Context = createContext(null);

export const BookingProviderV2 = ({ children }) => {
    const [submitting, setSubmitting] = useState(false);
    const { t: translate } = useTranslation();
    const { currency } = useCurrency();

    const addBooking = async ({ items = [], triper, contractId, paymentPlatformId, isPendingTour, overrideAvailability, hostAgent, additional }) => {
        try {
            setSubmitting(true);

            const itemsParsed = items?.map((item) => ({
                ...item?.formData,
                pricing: item?.pricing,
                overrideData: {
                    ...item?.formData?.overrideData,
                    currency,
                },
                targetCurrency: currency ?? null,
            }));
            const result = await BookingServiceV2.reserve({ items: itemsParsed, triper, contractId, paymentPlatformId, isPendingTour, overrideAvailability, hostAgent, additional });

            return result;
        } catch (error) {
            toast.error(translate('global_bookings.errors.add_booking'), {
                position: 'top-right',
                theme: 'colored',
            });
            console.error(error);
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const confirmBooking = async (data) => {
        try {
            setSubmitting(true);
            const result = await BookingServiceV2.booking(data);

            return result?.[0] || result;
        } catch (error) {
            toast.error(translate('global_bookings.errors.add_booking'), {
                position: 'top-right',
                theme: 'colored',
            });
            console.error(error);
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const confirmPaymentLink = async (data) => {
        try {
            setSubmitting(true);
            const result = await BookingServiceV2.notifyPaymentLink(data);

            return result;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const cancelBooking = async (paymentId) => {
        try {
            setSubmitting(true);
            const result = await BookingServiceV2.cancelBooking(paymentId);

            return result;
        } catch (error) {
            toast.error(translate('global_bookings.errors.add_booking'), {
                position: 'top-right',
                theme: 'colored',
            });
            console.error(error);
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const cancelPaymentLink = async (paymentLink) => {
        try {
            setSubmitting(true);
            const result = await BookingServiceV2.cancelPaymentLink(paymentLink);

            return result;
        } catch (error) {
            toast.error(translate('global_bookings.errors.add_booking'), {
                position: 'top-right',
                theme: 'colored',
            });
            console.error(error);
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <BookingV2Context.Provider
            value={{
                submitting,
                addBooking,
                confirmBooking,
                confirmPaymentLink,
                cancelBooking,
                cancelPaymentLink,
            }}
        >
            {children}
        </BookingV2Context.Provider>
    );
};

BookingProviderV2.displayName = 'BookingProviderV2';

export const useBookingsV2 = () => useContext(BookingV2Context);
